<script lang="ts">
  import { type Warehouse } from '../../lib/types';

  let {
    warehouses,
    onadd,
    onedit,
    ontoggleactive,
    onarchive,
    onlink,
    linkedCounts = {},
  }: {
    warehouses: Warehouse[];
    onadd: () => void;
    onedit: (warehouse: Warehouse) => void;
    ontoggleactive: (warehouse: Warehouse) => void;
    onarchive: (warehouse: Warehouse) => void;
    onlink?: (warehouse: Warehouse) => void;
    linkedCounts?: Record<string, number>;
  } = $props();
</script>

<div class="warehouses-section">
  <div class="warehouses-header">
    <h4>Warehouses / Locations</h4>
    <button class="btn-secondary btn-small" onclick={onadd}>
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
      </svg>
      Add Warehouse
    </button>
  </div>

  {#if warehouses.length === 0}
    <div class="warehouses-empty">
      <p>No warehouses yet. Add your first warehouse to start managing inventory locations.</p>
    </div>
  {:else}
    <div class="warehouses-list">
      {#each warehouses as warehouse}
        <div class="warehouse-card" class:inactive={!warehouse.isActive}>
          <div class="warehouse-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 21h18M3 10h18M3 10l9-7 9 7M4 10v11m16-11v11M8 14v3m4-3v3m4-3v3" />
            </svg>
          </div>
          <div class="warehouse-details">
            <span class="warehouse-name">{warehouse.name}</span>
            {#if warehouse.address}
              <span class="warehouse-address">{warehouse.address}</span>
            {/if}
          </div>
          <div class="warehouse-status">
            <span class="status-badge" class:active={warehouse.isActive}>
              {warehouse.isActive ? 'Active' : 'Inactive'}
            </span>
            {#if (linkedCounts[warehouse.id] ?? 0) > 1}
              <span class="linked-badge" title="Linked to {linkedCounts[warehouse.id]} businesses">
                <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                  <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 001.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
                </svg>
                {linkedCounts[warehouse.id]} businesses
              </span>
            {/if}
          </div>
          <div class="warehouse-actions">
            {#if onlink}
              <button class="btn-icon" title="Link to other businesses" onclick={() => onlink(warehouse)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 001.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
                </svg>
              </button>
            {/if}
            <button
              class="btn-icon"
              title={warehouse.isActive ? 'Deactivate' : 'Activate'}
              onclick={() => ontoggleactive(warehouse)}
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                {#if warehouse.isActive}
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clip-rule="evenodd" />
                {:else}
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
                {/if}
              </svg>
            </button>
            <button class="btn-icon" title="Edit warehouse" onclick={() => onedit(warehouse)}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
            <button class="btn-icon btn-danger" title="Archive warehouse" onclick={() => onarchive(warehouse)}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                <path fill-rule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  <p class="warehouses-note">
    Warehouses you create here will be available in the EQUOS app for inventory and order management.
  </p>
</div>

<style>
  .warehouses-section {
    background: var(--color-gray-50);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    padding: var(--space-5);
  }

  .warehouses-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-4);
  }

  .warehouses-header h4 {
    margin: 0;
    font-size: 1rem;
    color: var(--color-gray-800);
  }

  .warehouses-empty {
    text-align: center;
    padding: var(--space-8);
    color: var(--color-gray-500);
    font-size: 0.9rem;
  }

  .warehouses-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .warehouse-card {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    background: var(--color-white);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-sm);
    padding: var(--space-4);
  }

  .warehouse-card.inactive {
    opacity: 0.6;
  }

  .warehouse-icon {
    width: 40px;
    height: 40px;
    background: var(--color-gray-200);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .warehouse-icon svg {
    width: 20px;
    height: 20px;
    color: var(--color-gray-500);
  }

  .warehouse-details {
    flex: 1;
    min-width: 0;
  }

  .warehouse-name {
    display: block;
    font-weight: 500;
    color: var(--color-gray-800);
  }

  .warehouse-address {
    display: block;
    font-size: 0.85rem;
    color: var(--color-gray-500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .warehouse-status {
    flex-shrink: 0;
  }

  .linked-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.5rem;
    background: #ede9fe;
    color: #6d28d9;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 4px;
    margin-top: 0.25rem;
  }

  .linked-badge svg {
    width: 12px;
    height: 12px;
  }

  .warehouse-actions {
    display: flex;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .warehouses-note {
    margin: var(--space-4) 0 0;
    font-size: 0.8rem;
    color: var(--color-gray-500);
    text-align: center;
  }

  @media (max-width: 640px) {
    .warehouse-card {
      flex-wrap: wrap;
    }

    .warehouse-status {
      order: -1;
      width: 100%;
      margin-bottom: var(--space-2);
    }

    .warehouse-actions {
      width: 100%;
      justify-content: flex-end;
      padding-top: var(--space-3);
      border-top: 1px solid var(--color-gray-200);
    }
  }
</style>
